import { Button, Layout, Modal, PageHeader, Table, Input, Select, Space, Tag, Divider, Typography, Empty, Card, Row, Col } from 'antd';
import {
  ExclamationCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  RetweetOutlined,
  DeleteOutlined,
  DeleteColumnOutlined,
} from '@ant-design/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient, apiErrorMessage } from '../../../utils';
import { FailedQueueMessage } from '../../../models';

export function FailedQueueMessagesPage() {
  console.log('FailedQueueMessagesPage mounted');
  const { Content } = Layout;
  const { Text } = Typography;
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<FailedQueueMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [current, setCurrent] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedQueueName, setSelectedQueueName] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [queueNames, setQueueNames] = useState<string[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<FailedQueueMessage | null>(null);
  const [messageDetailsModalVisible, setMessageDetailsModalVisible] = useState(false);
  const [expandedRetryStack, setExpandedRetryStack] = useState<number | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const orderBy = 'createdAt';
  const order: 'ASC' | 'DESC' = 'DESC';

  const loadQueueNames = useCallback(() => {
    console.log('Loading queue names...');
    apiClient
      .get('queue/failed-messages/queue-names')
      .then(response => {
        console.log('Queue names loaded:', response.data);
        setQueueNames(response.data);
      })
      .catch((reason: any) => {
        console.error('Error loading queue names:', reason);
        apiErrorMessage(reason);
      });
  }, []);

  const loadMessages = useCallback(
    (page = 1, size = pageSize) => {
      console.log('Loading messages...', { page, size, search, selectedQueueName, selectedStatus });
      setLoading(true);
      const params: any = {
        limit: size,
        offset: (page - 1) * size,
        orderBy,
        order,
      };

      if (search) {
        params.search = search;
      }

      if (selectedQueueName) {
        params.queueName = selectedQueueName;
      }

      if (selectedStatus) {
        params.status = selectedStatus;
      }

      apiClient
        .get('queue/failed-messages', { params })
        .then(response => {
          console.log('Messages loaded:', response.data);
          setMessages(response.data.rows);
          setTotal(response.data.count);
          setLoading(false);
          setCurrent(page);
        })
        .catch((reason: any) => {
          console.error('Error loading messages:', reason);
          setLoading(false);
          apiErrorMessage(reason);
        });
    },
    [pageSize, search, selectedQueueName, selectedStatus, orderBy, order],
  );

  useEffect(() => {
    loadQueueNames();
  }, [loadQueueNames]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const showMessageDetails = (record: FailedQueueMessage) => {
    setSelectedMessage(record);
    setMessageDetailsModalVisible(true);
  };

  const confirmRetryMessage = (record: FailedQueueMessage) => {
    Modal.confirm({
      title: 'Mesajı yeniden dene',
      icon: <ExclamationCircleOutlined />,
      content: `"${record.queueName}" kuyruğundaki mesajı yeniden kuyruğa eklemek istediğinize emin misiniz?`,
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: () => retryMessage(record),
    });
  };

  const retryMessage = (record: FailedQueueMessage) => {
    setLoading(true);
    apiClient
      .post(`queue/failed-messages/${record.id}/retry`)
      .then(() => {
        setLoading(false);
        Modal.success({
          title: 'Başarılı',
          content: 'Mesaj başarıyla kuyruğa eklendi.',
          onOk: () => {
            loadMessages(current, pageSize);
          },
        });
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteMessage = (record: FailedQueueMessage) => {
    Modal.confirm({
      title: 'Mesajı sil',
      icon: <ExclamationCircleOutlined />,
      content: `Başarısız mesajı silmek istediğinize emin misiniz?`,
      okText: 'Evet',
      cancelText: 'Vazgeç',
      okButtonProps: { danger: true },
      onOk: () => deleteMessage(record),
    });
  };

  const deleteMessage = (record: FailedQueueMessage) => {
    setLoading(true);
    apiClient
      .delete(`queue/failed-messages/${record.id}`)
      .then(() => {
        setLoading(false);
        Modal.success({
          title: 'Başarılı',
          content: 'Mesaj başarıyla silindi.',
          onOk: () => {
            loadMessages(current, pageSize);
          },
        });
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmBulkDelete = () => {
    const selectedMessages = messages.filter(m => selectedRowKeys.includes(m.id));
    const pendingCount = selectedMessages.filter(m => m.status === 'pending').length;

    if (pendingCount > 0) {
      Modal.warning({
        title: 'Uyarı',
        content: `Seçilen ${selectedRowKeys.length} mesajdan ${pendingCount} tanesi "Beklemede" durumunda olduğu için silinemez. Sadece ${selectedRowKeys.length - pendingCount} mesaj silinecektir.`,
      });
    }

    Modal.confirm({
      title: 'Toplu silme',
      icon: <ExclamationCircleOutlined />,
      content: `${selectedRowKeys.length} adet mesajı silmek istediğinize emin misiniz?${pendingCount > 0 ? ` (${pendingCount} beklemedeki mesaj atlanacak)` : ''}`,
      okText: 'Evet',
      cancelText: 'Vazgeç',
      okButtonProps: { danger: true },
      onOk: () => bulkDeleteMessages(),
    });
  };

  const bulkDeleteMessages = () => {
    setLoading(true);
    apiClient
      .post('queue/failed-messages/bulk-delete', { messageIds: selectedRowKeys })
      .then((response) => {
        setLoading(false);
        const { deleted, skipped } = response.data;
        Modal.success({
          title: 'Başarılı',
          content: `${deleted} mesaj silindi.${skipped > 0 ? ` ${skipped} beklemedeki mesaj atlandı.` : ''}`,
          onOk: () => {
            setSelectedRowKeys([]);
            loadMessages(current, pageSize);
          },
        });
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'failed':
        return <Tag color="red">Başarısız</Tag>;
      case 'success':
        return <Tag color="green">Başarılı</Tag>;
      case 'pending':
        return <Tag color="orange">Beklemede</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const columns = [
    {
      title: 'Kuyruk',
      dataIndex: 'queueName',
      key: 'queueName',
      width: 150,
      sorter: (a: FailedQueueMessage, b: FailedQueueMessage) => a.queueName.localeCompare(b.queueName),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
      sorter: (a: FailedQueueMessage, b: FailedQueueMessage) => a.status.localeCompare(b.status),
    },
    {
      title: 'Son hata',
      dataIndex: 'lastErrorMessage',
      key: 'lastErrorMessage',
      width: 300,
      render: (text: string, record: FailedQueueMessage) => (
        <div style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{record.status === 'success' ? '-' : text}</div>
      ),
    },
    {
      title: 'İlk deneme',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('tr-TR'),
      sorter: (a: FailedQueueMessage, b: FailedQueueMessage) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Deneme sayısı',
      dataIndex: 'retryCount',
      key: 'retryCount',
      width: 100,
      sorter: (a: FailedQueueMessage, b: FailedQueueMessage) => a.retryCount - b.retryCount,
    },
    {
      title: 'Son deneme',
      dataIndex: 'lastRetryAt',
      key: 'lastRetryAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('tr-TR'),
      sorter: (a: FailedQueueMessage, b: FailedQueueMessage) =>
        new Date(a.lastRetryAt || a.createdAt).getTime() - new Date(b.lastRetryAt || b.createdAt).getTime(),
    },
    {
      key: 'actions',
      width: 120,
      render: (text: any, record: FailedQueueMessage) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => showMessageDetails(record)} title="Göster" />
          <Button
            size="small"
            icon={<RetweetOutlined />}
            onClick={() => confirmRetryMessage(record)}
            disabled={record.status === 'success'}
            title="Yeniden dene"
          />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => confirmDeleteMessage(record)}
            disabled={record.status === 'pending'}
            title={record.status === 'pending' ? 'Beklemedeki mesajlar silinemez' : 'Sil'}
          />
        </Space>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleSearchDebounced = useMemo(() => {
    let timeout: NodeJS.Timeout;
    return (value: string) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setCurrent(1);
        loadMessages(1, pageSize);
      }, 500);
    };
  }, [loadMessages, pageSize]);

  const handleQueueNameChange = (value: string | undefined) => {
    setSelectedQueueName(value);
    setCurrent(1);
  };

  const handleStatusChange = (value: string | undefined) => {
    setSelectedStatus(value);
    setCurrent(1);
  };

  return (
    <>
      <PageHeader
        title={
          <>
            <DeleteColumnOutlined /> Kuyruk mesajları
          </>
        }
        extra={[
          selectedRowKeys.length > 0 && (
            <Button
              key="bulk-delete"
              danger
              onClick={confirmBulkDelete}
              icon={<DeleteOutlined />}
            >
              Seçilenleri Sil ({selectedRowKeys.length})
            </Button>
          ),
          <Button key="refresh" onClick={() => loadMessages(1, pageSize)} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
        ]}
      />
      <Content>
        <Card style={{ marginBottom: '1rem' }}>
          <Row gutter={16}>
            <Col span={6}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Ara</label>
              <Input.Search
                placeholder="Ara..."
                onSearch={handleSearch}
                onChange={e => {
                  handleSearch(e.target.value);
                  handleSearchDebounced(e.target.value);
                }}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={6}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Kuyruk</label>
              <Select
                placeholder="Kuyruk seçin"
                value={selectedQueueName}
                onChange={handleQueueNameChange}
                style={{ width: '100%' }}
                allowClear
                showSearch
                filterOption={(input, option) => (option?.children as string)?.toLowerCase().includes(input.toLowerCase())}
              >
                {queueNames.map(queueName => (
                  <Select.Option key={queueName} value={queueName}>
                    {queueName}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Durum</label>
              <Select
                placeholder="Durum seçin"
                value={selectedStatus}
                onChange={handleStatusChange}
                style={{ width: '100%' }}
                allowClear
                showSearch
                filterOption={(input, option) => (option?.children as string)?.toLowerCase().includes(input.toLowerCase())}
              >
                <Select.Option value="failed">Başarısız</Select.Option>
                <Select.Option value="success">Başarılı</Select.Option>
                <Select.Option value="pending">Beklemede</Select.Option>
              </Select>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          rowKey={row => row.id}
          dataSource={messages}
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys: React.Key[]) => {
              setSelectedRowKeys(selectedKeys as string[]);
            },
            getCheckboxProps: (record: FailedQueueMessage) => ({
              disabled: record.status === 'pending',
              name: record.id,
            }),
          }}
          pagination={{
            current,
            pageSize,
            total,
            onChange: (page, size) => {
              const newPageSize = size ?? pageSize;
              setPageSize(newPageSize);
              setCurrent(page);
              loadMessages(page, newPageSize);
            },
            pageSizeOptions: ['50', '100', '200'],
            showSizeChanger: true,
            position: ['bottomCenter'],
            showTotal: (total, range) => {
              let start = range[0];
              if (start < 1) {
                start = 1;
              }
              let end = range[1];
              if (end === 0) {
                end = total;
              }
              return `Toplam ${total} mesaj. ${start}-${end} arası gösteriliyor.`;
            },
          }}
        />
      </Content>

      <Modal
        title="Mesaj detayları"
        visible={messageDetailsModalVisible}
        width={'80%'}
        onCancel={() => {
          setMessageDetailsModalVisible(false);
          setSelectedMessage(null);
        }}
        destroyOnClose={true}
        footer={
          selectedMessage ? (
            <Space>
              <Button
                type="primary"
                icon={<RetweetOutlined />}
                onClick={() => {
                  setMessageDetailsModalVisible(false);
                  retryMessage(selectedMessage);
                }}
                disabled={selectedMessage.status === 'success'}
              >
                Yeniden dene
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setMessageDetailsModalVisible(false);
                  confirmDeleteMessage(selectedMessage);
                }}
              >
                Sil
              </Button>
            </Space>
          ) : null
        }
      >
        {selectedMessage ? (
          <Content>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Kuyruk adı:</Text>
              <div style={{ marginTop: '8px' }}>{selectedMessage.queueName}</div>
            </div>

            <Divider />

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Durum:</Text>
              <div style={{ marginTop: '8px' }}>{getStatusTag(selectedMessage.status)}</div>
            </div>

            <Divider />

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Mesaj gövdesi:</Text>
              <div style={{ marginTop: '8px' }}>
                <div
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {try_parse_json(selectedMessage.messageBody)}
                </div>
              </div>
            </div>

            <Divider />

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Deneme sayısı:</Text>
              <div style={{ marginTop: '8px' }}>{selectedMessage.retryCount}</div>
            </div>

            <Divider />

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Son hata mesajı:</Text>
              <div style={{ marginTop: '8px', color: selectedMessage.status === 'success' ? 'inherit' : '#ff4d4f' }}>
                {selectedMessage.status === 'success' ? '-' : selectedMessage.lastErrorMessage || '-'}
              </div>
            </div>

            <Divider />

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Son hata log'u:</Text>
              <div style={{ marginTop: '8px' }}>
                <div
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {selectedMessage.status === 'success' ? '-' : selectedMessage.lastErrorStack || '-'}
                </div>
              </div>
            </div>

            <Divider />

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Deneme geçmişi:</Text>
              {selectedMessage.retries && selectedMessage.retries.length > 0 ? (
                <div style={{ marginTop: '8px' }}>
                  {selectedMessage.retries
                    .slice()
                    .reverse()
                    .map((retry, index: number) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: '12px',
                          padding: '8px',
                          borderRadius: '4px',
                          border: '1px solid #e8e8e8',
                        }}
                      >
                        <div>
                          <Text strong>Deneme {selectedMessage.retries.length - index}:</Text>{' '}
                          {new Date(retry.timestamp).toLocaleString('tr-TR')}
                        </div>
                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#666', marginBottom: '8px' }}>{retry.errorMessage}</div>
                        {retry.errorStack && (
                          <>
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, fontSize: '12px' }}
                              onClick={() => setExpandedRetryStack(expandedRetryStack === index ? null : index)}
                            >
                              {expandedRetryStack === index ? "Log'u gizle" : "Log'u göster"}
                            </Button>
                            {expandedRetryStack === index && (
                              <div
                                style={{
                                  marginTop: '8px',
                                  backgroundColor: '#f5f5f5',
                                  padding: '12px',
                                  borderRadius: '4px',
                                  fontFamily: 'monospace',
                                  fontSize: '12px',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-all',
                                }}
                              >
                                {retry.errorStack}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <Empty description="Deneme geçmişi yok" />
              )}
            </div>
          </Content>
        ) : (
          <Empty />
        )}
      </Modal>
    </>
  );
}

function try_parse_json(str: string): string {
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch (e) {
    return str;
  }
}
